import { useMemo } from 'react';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { useAppFlagStore } from '@frontend/shared';
import { useListAITaskSettingsQuery } from '../queries';
import { AITaskSetting } from '../types';

export const useAggregateTaskSettings = ({
  locationIds = [],
  locationId,
  enabled = true,
  checkTaskCategoriesAggregate = false,
}: {
  locationIds?: string[];
  locationId?: string;
  enabled?: boolean;
  checkTaskCategoriesAggregate?: boolean;
}) => {
  const { getFeatureFlagValue } = useAppFlagStore();

  const selectedLocationIds = useMemo(() => {
    if (locationId) {
      return [locationId];
    }
    return locationIds;
  }, [locationId, locationIds]);

  const isTaskSettingsEnabled = getFeatureFlagValue({
    locationIds: selectedLocationIds,
    flagName: 'call-intel:task-settings',
  });

  const { data: locationTaskSettingsMap, isLoading } = useListAITaskSettingsQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: enabled && isTaskSettingsEnabled && selectedLocationIds.length > 0,
      select: (data) => {
        return data.results?.reduce((acc, setting) => {
          acc[setting.locationId] = setting;
          return acc;
        }, {} as Record<string, AITaskSetting>);
      },
    },
  });

  const { isTaskCreationEnabled, taskCategoryEnabledMap } = useMemo(() => {
    const _isTaskCreationEnabled = selectedLocationIds.some((_locationId) => {
      // Enable task creation by default if the feature flag is off
      if (!isTaskSettingsEnabled) {
        return true;
      }
      const setting = locationTaskSettingsMap?.[_locationId];
      return setting ? setting.aiTaskCreationEnabled : false;
    });

    const _taskCategoryEnabledMap = (checkTaskCategoriesAggregate ? selectedLocationIds : []).reduce<
      Record<TaskType, boolean>
    >((acc, _locationId) => {
      const taskCategoriesMap = locationTaskSettingsMap?.[_locationId]?.aiTaskCategories;
      Object.entries(taskCategoriesMap || {}).forEach(([taskType, isEnabled]) => {
        acc[taskType as TaskType] ||= isEnabled;
      });
      return acc;
    }, {} as Record<TaskType, boolean>);

    return {
      isTaskCreationEnabled: _isTaskCreationEnabled,
      taskCategoryEnabledMap: _taskCategoryEnabledMap,
    };
  }, [locationTaskSettingsMap, selectedLocationIds, checkTaskCategoriesAggregate, isTaskSettingsEnabled]);

  return { locationTaskSettingsMap, isLoading, isTaskCreationEnabled, taskCategoryEnabledMap };
};
